import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, IconButton, Menu, MenuItem, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useCallback, useMemo } from 'react';

type TProps = {
  title: string | React.ReactNode;
  children: React.ReactNode;
  minHeight?: number;
  errors?: string;
  tooltipText?: string;
  editDataAction?: string;
  headerActions?: React.ReactNode;
  titleAlignment?: 'center' | 'flex-start';
} & (
  | {
      actions?: never;
      onEdit?: () => void;
    }
  | {
      actions?: { title: string; onClick?: () => void; disabled?: boolean }[];
      onEdit?: never;
    }
);

export const Section: React.FC<TProps> = ({
  title,
  errors,
  tooltipText,
  actions,
  onEdit,
  children,
  minHeight,
  editDataAction,
  headerActions,
  titleAlignment = 'center',
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const menuItems = useMemo(() => {
    return actions?.map(({ title: menuItemTitle, onClick, disabled }) => {
      return (
        <MenuItem
          key={menuItemTitle}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            if (onClick) {
              onClick();
            }
            handleClose();
          }}
        >
          {menuItemTitle}
        </MenuItem>
      );
    });
  }, [handleClose, actions]);

  return (
    <Paper elevation={0} square={false} sx={{ p: 3, width: 1, minHeight }}>
      <Box
        mb={2}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: titleAlignment,
          gap: 3,
          position: 'relative',
        }}
      >
        <Typography
          component="div"
          fontWeight={theme.typography.fontWeightMedium}
          sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          {title}
          {errors && (
            <Tooltip title={tooltipText} arrow placement="top">
              <ErrorOutlineIcon color="error" fontSize="small" />
            </Tooltip>
          )}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 0 auto' }}>
          {headerActions}
          {onEdit && (
            <IconButton size="small" onClick={onEdit} data-action={editDataAction}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {actions && (
            <>
              <IconButton size="small" onClick={handleClick} aria-label="Summary actions">
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
                {menuItems}
              </Menu>
            </>
          )}
        </Box>
      </Box>
      {children}
    </Paper>
  );
};
